import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  Loader2,
  MapPin,
  MessageCircle,
  Navigation,
  RefreshCw,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const severityMeta = {
  missing: {
    label: 'Sin ubicacion',
    chip: 'bg-slate-900 text-white border-slate-900',
  },
  low: {
    label: 'Bajo',
    chip: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  medium: {
    label: 'Medio',
    chip: 'bg-orange-100 text-orange-800 border-orange-300',
  },
  high: {
    label: 'Alto',
    chip: 'bg-red-100 text-red-800 border-red-300',
  },
};

function formatDistance(meters) {
  if (meters == null) return 'Sin ubicacion guardada';
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${Math.round(meters)} m`;
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function googleMapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function MapCompareModal({ alert, onClose, onApplyLocation }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const delivery = alert.delivery_point;
  const hasDeliveryLocation = delivery.latitude != null && delivery.longitude != null;

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const firstLocation = alert.locations[0];
    const initialLat = hasDeliveryLocation ? delivery.latitude : firstLocation?.latitude;
    const initialLng = hasDeliveryLocation ? delivery.longitude : firstLocation?.longitude;
    if (initialLat == null || initialLng == null) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([initialLat, initialLng], 15);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [alert.locations, delivery.latitude, delivery.longitude, hasDeliveryLocation]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const bounds = L.latLngBounds([]);
    if (hasDeliveryLocation) {
      const deliveryMarker = L.marker([delivery.latitude, delivery.longitude], {
        icon: L.divIcon({
          className: 'delivery-location-marker',
          html: '<div style="width:28px;height:28px;border-radius:50%;background:#111827;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:12px;">DP</div>',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      }).addTo(map);
      deliveryMarker.bindPopup(`<strong>Ubicacion actual entrega #${delivery.id}</strong>`);
      markersRef.current.push(deliveryMarker);
      bounds.extend([delivery.latitude, delivery.longitude]);
    }

    alert.locations.forEach((location, index) => {
      const isLatest = index === 0;
      const color = location.severity === 'missing' ? '#111827' : location.severity === 'high' ? '#DC2626' : location.severity === 'medium' ? '#F97316' : '#CA8A04';
      const marker = L.marker([location.latitude, location.longitude], {
        icon: L.divIcon({
          className: 'whatsapp-location-marker',
          html: `<div style="width:${isLatest ? 30 : 24}px;height:${isLatest ? 30 : 24}px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:11px;">WA</div>`,
          iconSize: [isLatest ? 30 : 24, isLatest ? 30 : 24],
          iconAnchor: [isLatest ? 15 : 12, isLatest ? 15 : 12],
        }),
      }).addTo(map);
      marker.on('click', () => setSelectedLocation(location));
      marker.bindPopup(`<strong>WhatsApp</strong><br/>${formatDistance(location.distance_meters)}`);
      markersRef.current.push(marker);
      bounds.extend([location.latitude, location.longitude]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.25), { maxZoom: 16 });
    }

    setTimeout(() => map.invalidateSize(), 100);
  }, [alert.locations, delivery.id, delivery.latitude, delivery.longitude, hasDeliveryLocation]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-2 sm:p-3">
      <div className="flex max-h-[calc(100dvh-1rem)] sm:max-h-[92dvh] min-h-0 w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-900">Comparar ubicaciones #{delivery.id}</h3>
            <p className="truncate text-xs text-slate-500">{delivery.recipient_name} - {delivery.recipient_phone}</p>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[1fr_280px] lg:overflow-hidden">
          <div ref={mapRef} className="h-[35dvh] min-h-[180px] bg-slate-200 sm:h-[55dvh] sm:min-h-[360px] lg:h-[70dvh]" />
          <div className="flex min-h-0 flex-col border-t border-slate-200 lg:border-l lg:border-t-0">
            <div className="border-b border-slate-200 p-3">
              <p className="text-xs font-bold uppercase text-slate-500">Seleccion</p>
              {selectedLocation ? (
                <div className="mt-2 space-y-2">
                  <span className={`inline-flex rounded border px-2 py-1 text-xs font-semibold ${severityMeta[selectedLocation.severity]?.chip}`}>
                    {severityMeta[selectedLocation.severity]?.label} - {formatDistance(selectedLocation.distance_meters)}
                  </span>
                  <p className="text-xs text-slate-600">{formatDateTime(selectedLocation.timestamp)}</p>
                  <div className="flex gap-2">
                    <a
                      href={googleMapsUrl(selectedLocation.latitude, selectedLocation.longitude)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-slate-300 px-2 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Maps
                    </a>
                    <button
                      onClick={() => onApplyLocation(alert, selectedLocation)}
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-[#B1336E] px-2 py-2 text-xs font-semibold text-white hover:bg-[#96295d]"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Usar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500">Haz click en un marcador WhatsApp para usarlo.</p>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              <p className="mb-2 text-xs font-bold uppercase text-slate-500">Ubicaciones WhatsApp</p>
              <div className="space-y-2">
                {alert.locations.map((location) => (
                  <button
                    key={location.id}
                    onClick={() => setSelectedLocation(location)}
                    className={`w-full rounded-md border p-2 text-left text-xs transition-colors ${
                      selectedLocation?.id === location.id ? 'border-[#B1336E] bg-[#B1336E]/5' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-slate-800">{formatDistance(location.distance_meters)}</span>
                      <span className={`rounded border px-1.5 py-0.5 font-semibold ${severityMeta[location.severity]?.chip}`}>
                        {severityMeta[location.severity]?.label}
                      </span>
                    </div>
                    <p className="mt-1 text-slate-500">{formatDateTime(location.timestamp)}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhatsAppLocationsModal({ selectedDate, onClose, onAlertCountChange }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [mapAlert, setMapAlert] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/control_tower/whatsapp-location-alerts/${selectedDate}`);
      const items = response.data.items || [];
      setAlerts(items);
      onAlertCountChange?.(items.length);
    } catch (error) {
      console.error('Error fetching WhatsApp location alerts:', error);
      setAlerts([]);
      onAlertCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onAlertCountChange, selectedDate]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const filteredAlerts = useMemo(() => {
    if (severityFilter === 'all') return alerts;
    return alerts.filter((alert) => alert.severity === severityFilter);
  }, [alerts, severityFilter]);

  const counts = useMemo(() => {
    return alerts.reduce(
      (acc, alert) => {
        acc.all += 1;
        acc[alert.severity] = (acc[alert.severity] || 0) + 1;
        return acc;
      },
      { all: 0, low: 0, medium: 0, high: 0 }
    );
  }, [alerts]);

  async function reviewAlert(alert, location, status) {
    const message =
      status === 'applied'
        ? `Actualizar la ubicacion de entrega #${alert.delivery_point.id} con esta ubicacion enviada por WhatsApp?`
        : status === 'ignored'
        ? `Ignorar esta alerta de ubicacion para entrega #${alert.delivery_point.id}?`
        : `Marcar esta alerta como revisada?`;

    if (!window.confirm(message)) return;

    setActionLoading(`${alert.delivery_point.id}-${location.id}-${status}`);
    try {
      await axios.post(
        `${API_URL}/api/control_tower/whatsapp-location-alerts/${alert.delivery_point.id}/review`,
        {
          whatsapp_message_id: location.id,
          review_status: status,
        }
      );
      await fetchAlerts();
      if (status === 'applied') {
        setMapAlert(null);
      }
    } catch (error) {
      console.error('Error reviewing WhatsApp location alert:', error);
      alert('No se pudo guardar la revision');
    } finally {
      setActionLoading(null);
    }
  }

  function latestLocation(alert) {
    return [...alert.locations].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-2 sm:p-3">
      <div className="flex max-h-[calc(100dvh-1rem)] sm:max-h-[92dvh] min-h-0 w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Navigation className="h-5 w-5 text-[#B1336E]" />
              Ubicaciones WhatsApp
            </h2>
            <p className="text-xs text-slate-500">Alertas cuando el cliente envio una ubicacion lejos de la ruta guardada.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAlerts}
              disabled={loading}
              className="rounded-md border border-slate-300 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex gap-2 overflow-x-auto">
            {['all', 'missing', 'high', 'medium', 'low'].map((severity) => (
              <button
                key={severity}
                onClick={() => setSeverityFilter(severity)}
                className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  severityFilter === severity
                    ? 'border-[#B1336E] bg-[#B1336E] text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {severity === 'all' ? 'Todos' : severityMeta[severity].label} ({counts[severity] || 0})
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex h-64 items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Cargando ubicaciones...
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <MapPin className="mb-3 h-12 w-12 text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">No hay conflictos de ubicacion</p>
              <p className="text-xs text-slate-500">Las ubicaciones WhatsApp estan dentro del radio o ya fueron revisadas.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAlerts.map((alertItem) => {
                const delivery = alertItem.delivery_point;
                const latest = latestLocation(alertItem);
                const loadingKey = latest ? `${delivery.id}-${latest.id}-ignored` : null;

                return (
                  <div key={delivery.id} className="rounded-md border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="font-bold text-[#B1336E]">#{delivery.id}</span>
                          <span className={`rounded border px-2 py-0.5 text-xs font-semibold ${severityMeta[alertItem.severity]?.chip}`}>
                            {severityMeta[alertItem.severity]?.label}
                          </span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                            {alertItem.severity === 'missing' ? 'Pendiente de ubicar' : `Max ${formatDistance(alertItem.max_distance_meters)}`}
                          </span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                            {alertItem.locations_count} ubicacion{alertItem.locations_count !== 1 ? 'es' : ''}
                          </span>
                        </div>
                        <p className="truncate text-sm font-semibold text-slate-900">{delivery.recipient_name}</p>
                        <p className="text-xs text-slate-500">{delivery.recipient_phone}</p>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-600">{delivery.address || 'Sin direccion'}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Ultima ubicacion WhatsApp: {formatDateTime(alertItem.latest_location_timestamp)} - {formatDistance(alertItem.latest_distance_meters)}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        <button
                          onClick={() => setMapAlert(alertItem)}
                          className="inline-flex items-center gap-1 rounded-md bg-[#B1336E] px-3 py-2 text-xs font-semibold text-white hover:bg-[#96295d]"
                        >
                          <MapPin className="h-4 w-4" />
                          Comparar mapa
                        </button>
                        <a
                          href={googleMapsUrl(delivery.latitude, delivery.longitude)}
                          target="_blank"
                          rel="noreferrer"
                          className={`inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold ${
                            delivery.latitude == null || delivery.longitude == null
                              ? 'pointer-events-none text-slate-300'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <ExternalLink className="h-4 w-4" />
                          Actual
                        </a>
                        {latest && (
                          <button
                            onClick={() => reviewAlert(alertItem, latest, 'ignored')}
                            disabled={actionLoading === loadingKey}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {actionLoading === loadingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                            Ignorar
                          </button>
                        )}
                        {delivery.chat_id && (
                          <button
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-400"
                            title="El chat se abre desde el modulo de chats"
                          >
                            <MessageCircle className="h-4 w-4" />
                            Chat
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {mapAlert && (
        <MapCompareModal
          alert={mapAlert}
          onClose={() => setMapAlert(null)}
          onApplyLocation={(alertItem, location) => reviewAlert(alertItem, location, 'applied')}
        />
      )}
    </div>
  );
}

export default WhatsAppLocationsModal;
